(()=>{if(customElements.get('tm-ember-motion-himero'))return;class TmEmberMotion extends HTMLElement{connectedCallback(){this.abortController?.abort();this.abortController=new AbortController();this.setupMotionProof()}disconnectedCallback(){this.abortController?.abort()}setupMotionProof() {
      const section = this.querySelector('.he-tech-details');
      const panels = section ? Array.from(section.querySelectorAll('[data-he-proof-panel]')) : [];
      if (!section || panels.length !== 5) return;

      const chapters = panels.map((panel) => {
        const video = panel.querySelector('[data-he-proof-chapter-video]');
        const source = video?.querySelector('source[data-src]');
        const button = panel.querySelector('[data-he-proof-play]');
        const status = panel.querySelector('[data-he-proof-video-status]');
        return { panel, video, source, button, status, manualOnly: panel.hasAttribute('data-he-proof-manual'), loaded: false, pending: false, failed: false, blocked: false, userPaused: false, request: 0 };
      });
      if (chapters.some((chapter) => chapter.video
        && (!chapter.source?.dataset.src || !chapter.button || !chapter.status))) return;
      const videoChapters = chapters.filter((chapter) => chapter.video);
      if (!videoChapters.length) return;
      this.motionObserver?.disconnect();
      this.proofVideos?.forEach((video) => video.pause());
      this.proofVideos = videoChapters.map((chapter) => chapter.video);
      const signal = this.abortController.signal;
      const labels = {
        play: section.dataset.hePlayLabel || 'Play video',
        pause: section.dataset.hePauseLabel || 'Pause video',
        error: section.dataset.heVideoErrorLabel || 'This video could not play. Press Play to try again.',
      };
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const canAutoplay = () => !reducedMotion.matches
        && !connection?.saveData
        && !/(^|-)2g$/.test(connection?.effectiveType || '');
      let owner = null;
      let manualOwner = null;
      let frame = 0;
      let observer = null;

      const measure = (chapter) => {
        if (!section.isConnected || chapter.panel.hidden || !chapter.video.getClientRects().length) return null;
        const rect = chapter.video.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        if (!rect.width || !rect.height || rect.right <= 0 || rect.left >= window.innerWidth) return null;
        const visibleHeight = Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0));
        return {
          visible: visibleHeight > 0,
          ratio: visibleHeight / Math.min(rect.height, viewportHeight),
          distance: Math.abs((rect.top + rect.bottom) / 2 - viewportHeight / 2),
        };
      };
      const syncButton = (chapter) => {
        const playing = chapter.pending || (!chapter.video.paused && !chapter.video.ended);
        chapter.button.textContent = playing ? labels.pause : labels.play;
        chapter.button.setAttribute('aria-label', playing ? labels.pause : labels.play);
        chapter.button.setAttribute('aria-pressed', String(playing));
        chapter.button.setAttribute('aria-busy', String(chapter.pending));
      };
      const pauseChapter = (chapter) => {
        const needsCancellation = chapter.pending || !chapter.video.paused;
        if (needsCancellation) chapter.request += 1;
        chapter.pending = false;
        if (needsCancellation) chapter.video.pause();
        if (needsCancellation && !chapter.failed && !chapter.blocked
          && chapter.status.textContent === (section.dataset.heLoadingLabel || 'Loading video…')) {
          chapter.status.hidden = true;
          chapter.status.textContent = '';
        }
        syncButton(chapter);
      };
      const pauseOtherVideos = (keep = null) => videoChapters.forEach((chapter) => {
        if (chapter !== keep) pauseChapter(chapter);
      });
      const prepareVideo = (chapter, manual = false) => {
        if (signal.aborted || (!manual && (chapter.manualOnly || !canAutoplay() || chapter.failed))) return false;
        if (!chapter.loaded || chapter.failed) {
          chapter.source.src = chapter.source.dataset.src;
          chapter.video.preload = manual ? 'auto' : 'metadata';
          chapter.video.muted = true;
          chapter.failed = false;
          chapter.loaded = true;
          chapter.video.load();
        }
        if (manual) chapter.video.preload = 'auto';
        return true;
      };
      const mayContinue = (chapter, manual) => owner === chapter && !signal.aborted
        && !document.hidden && measure(chapter)?.visible
        && (manual || (canAutoplay() && !chapter.userPaused && !chapter.failed && !chapter.blocked));
      const playVideo = async (chapter, manual = false) => {
        if (signal.aborted || document.hidden || !measure(chapter)?.visible) return;
        if (!manual && (!canAutoplay() || chapter.userPaused || chapter.failed || chapter.blocked)) return;
        owner = chapter;
        pauseOtherVideos(chapter);
        if (chapter.pending || (!chapter.video.paused && !chapter.video.ended)) return;
        if (!prepareVideo(chapter, manual)) return;
        chapter.status.textContent = (section.dataset.heLoadingLabel || 'Loading video…');
        chapter.status.hidden = false;
        chapter.pending = true;
        const request = ++chapter.request;
        syncButton(chapter);
        try {
          await chapter.video.play();
          if (request !== chapter.request) {
            if (owner !== chapter || signal.aborted || document.hidden) chapter.video.pause();
            return;
          }
          chapter.pending = false;
          if (!mayContinue(chapter, manual)) {
            pauseChapter(chapter);
            return;
          }
          chapter.status.hidden = true;
          chapter.status.textContent = '';
          chapter.panel.classList.add('is-demonstrating');
          syncButton(chapter);
        } catch (error) {
          if (request !== chapter.request || signal.aborted) return;
          chapter.pending = false;
          chapter.blocked = true;
          if (owner === chapter) owner = null;
          if (manualOwner === chapter) manualOwner = null;
          syncButton(chapter);
          chapter.status.textContent = error?.name === 'NotAllowedError' ? labels.play : labels.error;
          chapter.status.hidden = false;
        }
      };
      const reconcile = () => {
        frame = 0;
        if (signal.aborted) return;
        if (document.hidden) {
          owner = null;
          manualOwner = null;
          pauseOtherVideos();
          return;
        }
        const positions = videoChapters.map((chapter) => ({ chapter, geometry: measure(chapter) }));
        if (manualOwner && ((measure(manualOwner)?.ratio || 0) < .15 || manualOwner.userPaused
          || manualOwner.failed || manualOwner.blocked)) manualOwner = null;
        const candidate = canAutoplay() ? positions
          .filter(({ chapter, geometry }) => !chapter.manualOnly && geometry?.visible && geometry.ratio >= .25
            && !chapter.userPaused && !chapter.failed && !chapter.blocked)
          .sort((a, b) => a.geometry.distance - b.geometry.distance)[0]?.chapter : null;
        const next = manualOwner || candidate || null;
        if (owner !== next) {
          owner = next;
          pauseOtherVideos(next);
        } else pauseOtherVideos(next);
        if (next) playVideo(next, next === manualOwner);
      };
      const schedule = () => {
        if (!signal.aborted && !frame) frame = requestAnimationFrame(reconcile);
      };

      videoChapters.forEach((chapter) => {
        chapter.button.addEventListener('click', () => {
          if (chapter.pending || (!chapter.video.paused && !chapter.video.ended)) {
            chapter.userPaused = true;
            if (manualOwner === chapter) manualOwner = null;
            if (owner === chapter) owner = null;
            pauseChapter(chapter);
            chapter.status.hidden = true;
          } else {
            chapter.userPaused = false;
            chapter.blocked = false;
            chapter.panel.classList.add('is-demonstrating');
            manualOwner = chapter;
            playVideo(chapter, true);
          }
        }, { signal });
        const imageToggle = chapter.panel.querySelector('[data-he-proof-image]');
        if (imageToggle) {
          imageToggle.hidden = false;
          imageToggle.addEventListener('click', () => {
            chapter.userPaused = true;
            if (manualOwner === chapter) manualOwner = null;
            if (owner === chapter) owner = null;
            pauseChapter(chapter);
            chapter.panel.classList.remove('is-demonstrating');
            chapter.status.hidden = true;
            chapter.button.focus();
          }, { signal });
        }
        chapter.video.addEventListener('play', () => {
          if (!mayContinue(chapter, manualOwner === chapter)) {
            pauseChapter(chapter);
            return;
          }
          pauseOtherVideos(chapter);
          syncButton(chapter);
        }, { signal });
        chapter.video.addEventListener('pause', () => {
          if (chapter.video.paused && chapter.pending) {
            chapter.request += 1;
            chapter.pending = false;
          }
          syncButton(chapter);
        }, { signal });
        chapter.video.addEventListener('ended', () => {
          chapter.pending = false;
          syncButton(chapter);
        }, { signal });
        chapter.video.addEventListener('loadeddata', () => {
          chapter.failed = false;
          if (!chapter.pending && !chapter.blocked) {
            chapter.status.hidden = true;
            chapter.status.textContent = '';
          }
          syncButton(chapter);
        }, { signal });
        const showVideoError = () => {
          if (!chapter.loaded) return;
          chapter.failed = true;
          chapter.blocked = true;
          if (owner === chapter) owner = null;
          if (manualOwner === chapter) manualOwner = null;
          pauseChapter(chapter);
          chapter.status.textContent = labels.error;
          chapter.status.hidden = false;
        };
        chapter.video.addEventListener('error', showVideoError, { signal });
        chapter.source.addEventListener('error', showVideoError, { signal });
        syncButton(chapter);
        chapter.video.controls = false;
        chapter.button.hidden = false;
      });

      window.addEventListener('resize', schedule, { passive: true, signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          owner = null;
          manualOwner = null;
          pauseOtherVideos();
        } else schedule();
      }, { signal });
      reducedMotion.addEventListener?.('change', schedule, { signal });
      connection?.addEventListener?.('change', schedule, { signal });
      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver((entries) => {
          if (signal.aborted) return;
          entries.forEach((entry) => {
            const chapter = videoChapters.find((item) => item.video === entry.target);
            if (chapter && entry.isIntersecting && entry.intersectionRatio >= .1
              && canAutoplay() && !chapter.loaded && !chapter.failed) prepareVideo(chapter);
          });
          schedule();
        }, { rootMargin: '0px', threshold: [0, .1, .2, .3, .4, .5, .6, .7, .8, .9, 1] });
        videoChapters.forEach((chapter) => observer.observe(chapter.video));
        this.motionObserver = observer;
      }
      signal.addEventListener('abort', () => {
        if (frame) cancelAnimationFrame(frame);
        frame = 0;
        owner = null;
        manualOwner = null;
        observer?.disconnect();
        pauseOtherVideos();
      }, { once: true });
      schedule();
    }}customElements.define('tm-ember-motion-himero',TmEmberMotion)})();